import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, QrCode, Building2, ChevronRight, CheckCircle, Loader2, Crown } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice } from '@/lib/helpers'

type PaymentMethod = 'qpay' | 'bank'

const paymentMethods: {
  id: PaymentMethod
  label: string
  description: string
  icon: typeof QrCode
}[] = [
  {
    id: 'qpay',
    label: 'QPAY',
    description: 'Бүх банкны аппликейшнаар төлөх',
    icon: QrCode,
  },
  {
    id: 'bank',
    label: 'Дансаар шилжүүлэх',
    description: 'Банкны дансруу шууд шилжүүлэх',
    icon: Building2,
  },
]

export default function Payment() {
  const { id, plan: planParam } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('qpay')
  const [course, setCourse] = useState<any>(null)
  const [planInfo, setPlanInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [payment, setPayment] = useState<any>(null)
  const [paid, setPaid] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [actualLessonCount, setActualLessonCount] = useState(0)
  const isPremium = location.pathname.startsWith('/payment/premium/')

  useEffect(() => {
    if (isPremium && planParam) {
      api.premium.getPlans()
        .then((plans: any) => {
          const found = plans.find((p: any) => p.id === planParam)
          if (found) setPlanInfo(found)
        })
        .catch(() => {})
        .finally(() => setLoading(false))
    } else if (id) {
      Promise.all([
        api.courses.getById(id).then(setCourse),
        api.courses.getLessons(id).then((lessons: any[]) => setActualLessonCount(lessons.length)).catch(() => {}),
      ]).finally(() => setLoading(false))
    }
  }, [id, planParam, isPremium])

  // Polling — QPay төлбөр шалгах
  useEffect(() => {
    if (payment && payment.method === 'qpay' && !paid) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.payments.verify(payment._id)
          if (res.paid) {
            setPaid(true)
            if (pollRef.current) clearInterval(pollRef.current)
            if (isPremium) {
              setTimeout(() => navigate('/settings/premium'), 2000)
            } else {
              setTimeout(() => navigate(`/courses/${course?._id || ''}`), 2000)
            }
          }
        } catch {}
      }, 5000)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [payment, paid, navigate, course, isPremium])

  const handlePay = async () => {
    if (!isPremium && !course) return
    if (isPremium && !planInfo) return
    setPaying(true)

    try {
      const body = isPremium
        ? {
            type: 'premium',
            plan: planParam,
            amount: planInfo.price,
            method: selectedMethod,
          }
        : {
            type: 'course',
            courseId: course._id,
            amount: course.price,
            method: selectedMethod,
          }
      const res = await api.payments.initiate(body)
      setPayment(res)
    } catch {
      setPaying(false)
    }
  }

  const price = isPremium ? (planInfo?.price || 0) : (course?.price || 0)
  const title = isPremium ? (planInfo?.name || 'Premium') : (course?.title || 'Сургалт')
  const subtitle = isPremium ? 'Premium эрх' : 'Багц сургалт'
  const description = isPremium
    ? (planInfo?.description || '')
    : (course?.description?.slice(0, 80) || '')
  const lessonCount = actualLessonCount || course?.lessonCount || 0
  const qpay = payment?.qpay

  // Duration label for premium
  const durationLabel = isPremium && planInfo
    ? planInfo.duration >= 365
      ? `${Math.round(planInfo.duration / 30)} сар`
      : planInfo.duration >= 28
        ? `${Math.round(planInfo.duration / 30)} сар`
        : `${planInfo.duration} хоног`
    : null

  // Төлбөр амжилттай
  if (paid) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center p-6">
        <div className="bg-gray-900 rounded-3xl p-10 flex flex-col items-center gap-6 max-w-sm text-center">
          <div className="size-20 bg-green-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="size-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Төлбөр амжилттай!</h2>
          <p className="text-sm text-gray-400">
            {isPremium
              ? 'Таныг Premium тохиргоо руу шилжүүлж байна...'
              : 'Таныг сургалт руу шилжүүлж байна...'}
          </p>
        </div>
      </div>
    )
  }

  // QPay invoice үүссэн — QR + банк deeplinks харуулах
  if (payment && payment.method === 'qpay' && qpay) {
    return (
      <div className="min-h-screen bg-gray-800">
        <div className="max-w-[520px] mx-auto px-4 py-8 flex flex-col gap-6">
          <button
            onClick={() => { setPayment(null); setPaying(false) }}
            className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition self-start"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </button>

          {/* QR Code */}
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 flex flex-col items-center gap-5">
            <h2 className="text-lg font-bold text-white">QR кодоо уншуулна уу</h2>
            <div className="bg-white rounded-xl p-3">
              {qpay.qrImage ? (
                <img
                  src={`data:image/png;base64,${qpay.qrImage}`}
                  alt="QR Code"
                  className="size-[200px]"
                />
              ) : (
                <QrCode className="size-[200px] text-gray-300" />
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="size-4 animate-spin" />
              Төлбөр хүлээж байна...
            </div>
            <p className="text-2xl font-bold text-white">{formatPrice(price)}</p>
          </div>

          {/* Bank app deeplinks */}
          {qpay.urls && qpay.urls.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 flex flex-col gap-4">
              <p className="text-sm font-semibold text-gray-400 uppercase">Банкны апп-аар төлөх</p>
              <div className="grid grid-cols-4 gap-3">
                {qpay.urls.map((bank: any, i: number) => (
                  <a
                    key={i}
                    href={bank.link}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-800 transition"
                  >
                    {bank.logo ? (
                      <img src={bank.logo} alt={bank.name} className="size-10 rounded-lg" />
                    ) : (
                      <div className="size-10 rounded-lg bg-gray-700 flex items-center justify-center text-xs text-gray-400">
                        {bank.name?.[0]}
                      </div>
                    )}
                    <span className="text-[11px] text-gray-300 text-center leading-tight line-clamp-2">
                      {bank.description || bank.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Bank transfer — payment үүссэн
  if (payment && payment.method === 'bank') {
    return (
      <div className="min-h-screen bg-gray-800">
        <div className="max-w-[520px] mx-auto px-4 py-8 flex flex-col gap-6">
          <button
            onClick={() => { setPayment(null); setPaying(false) }}
            className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition self-start"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </button>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 flex flex-col gap-5">
            <h2 className="text-lg font-bold text-white">Дансны мэдээлэл</h2>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between py-2.5 border-b border-gray-700">
                <span className="text-sm text-gray-400">Банк</span>
                <span className="text-sm font-semibold text-white">Хаан банк</span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-700">
                <span className="text-sm text-gray-400">Дансны дугаар</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white font-mono">5089******89</span>
                  <button
                    onClick={() => navigator.clipboard.writeText('5089******89')}
                    className="text-xs text-primary-400 hover:text-primary-300 font-medium"
                  >
                    Хуулах
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-700">
                <span className="text-sm text-gray-400">Хүлээн авагч</span>
                <span className="text-sm font-semibold text-white">ARYA LLC</span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-gray-700">
                <span className="text-sm text-gray-400">Шилжүүлэх дүн</span>
                <span className="text-sm font-semibold text-primary-400">{formatPrice(price)}</span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-400">Гүйлгээний утга</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-primary-400 font-mono">{payment._id?.slice(-8)}</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(payment._id?.slice(-8) || '')}
                    className="text-xs text-primary-400 hover:text-primary-300 font-medium"
                  >
                    Хуулах
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Гүйлгээний утга дээр дээрх кодыг заавал бичнэ үү. Төлбөр баталгаажихад 1-24 цаг хүртэл хугацаа шаардагдана.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Төлбөрийн арга сонгох анхны хуудас
  return (
    <div className="min-h-screen bg-gray-800">
      <div className="max-w-[1024px] mx-auto flex flex-col lg:flex-row min-h-screen">
      {/* Left column - Payment methods */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="flex flex-col gap-8 w-full">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition cursor-pointer self-start px-4 py-2.5 rounded-full"
          >
            <ArrowLeft className="size-5" />
            Буцах
          </button>

          <h2 className="text-lg font-bold text-gray-200 uppercase tracking-wide">
            Төлбөрийн сонголт
          </h2>

          {/* Payment methods */}
          <div className="flex flex-col gap-4">
            {paymentMethods.map((method) => {
              const isSelected = selectedMethod === method.id
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className={`flex items-center gap-2.5 rounded-2xl p-4 border transition text-left cursor-pointer ${
                    isSelected
                      ? 'bg-gray-700 border-primary-500'
                      : 'bg-gray-800 border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className={`size-[46px] rounded-lg flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-primary-500' : 'bg-gray-700'
                  }`}>
                    <method.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <p className="text-lg font-bold text-white">{method.label}</p>
                    <p className="text-sm text-gray-400">{method.description}</p>
                  </div>
                  <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-white'
                  }`}>
                    {isSelected && <div className="size-2 rounded-full bg-primary-600" />}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Right column - Order summary */}
      <div className="w-full lg:w-[420px] bg-gray-900 p-8 lg:p-10 flex flex-col gap-10 lg:gap-16 shrink-0">
        {/* Product info */}
        <div className="flex items-center gap-3">
          <div className={`rounded-2xl size-[46px] flex items-center justify-center shrink-0 ${
            isPremium ? 'bg-accent-500' : 'bg-primary-500'
          }`}>
            {isPremium ? (
              <Crown className="size-6 text-white" />
            ) : (
              <CheckCircle className="size-6 text-white" />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs text-gray-300 uppercase">{subtitle}</p>
            <p className="text-lg font-bold text-gray-200 uppercase">
              {loading ? '...' : title}
            </p>
          </div>
        </div>

        {/* Price */}
        <div className="flex flex-col gap-9">
          <p className="text-lg font-bold text-gray-600 uppercase">Нийт төлөх дүн</p>
          <div className="flex flex-col gap-6">
            <div className="flex items-end gap-1">
              <span className="text-5xl font-bold text-white">{price.toLocaleString()}</span>
              <span className="text-[32px] font-bold text-primary-500">₮</span>
            </div>
            <p className="text-base font-medium text-gray-500">
              {description}
            </p>
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-col gap-[26px]">
          <div className="flex items-start justify-between pb-[26px] border-b border-gray-600">
            <span className="text-lg font-semibold text-gray-600">Хугацаа</span>
            <span className="text-base font-semibold text-white">
              {isPremium && durationLabel ? durationLabel : 'Насан туршийн эрх'}
            </span>
          </div>
          {!isPremium && lessonCount > 0 && (
            <div className="flex items-start justify-between pb-[26px] border-b border-gray-600">
              <span className="text-lg font-semibold text-gray-600">Хичээлийн тоо</span>
              <span className="text-base font-semibold text-white">{lessonCount} Хичээл</span>
            </div>
          )}
          {isPremium && (
            <div className="flex items-start justify-between pb-[26px] border-b border-gray-600">
              <span className="text-lg font-semibold text-gray-600">Эрх</span>
              <span className="text-base font-semibold text-white">Бүх premium контент</span>
            </div>
          )}
        </div>

        {/* Pay button */}
        <div className="flex-1 flex flex-col gap-6 justify-end items-center">
          <button
            onClick={handlePay}
            disabled={paying}
            className={`w-full h-[65px] rounded-full flex items-center justify-center gap-3 text-xl font-semibold transition cursor-pointer ${
              paying
                ? 'bg-gray-800 text-gray-500'
                : 'bg-primary-500 border border-primary-500 text-white shadow-xs hover:bg-primary-600'
            }`}
          >
            Төлөх: {formatPrice(price)}
            {paying ? (
              <Loader2 className="size-6 animate-spin" />
            ) : (
              <ChevronRight className="size-6" />
            )}
          </button>
          <p className="text-sm font-semibold text-gray-600 text-center uppercase">
            "Төлөх" товчийг дарснаар та ARYA-ийн үйлчилгээний нөхцөл болон төлбөрийн журмыг зөвшөөрч буйд тооцогдоно.
          </p>
        </div>
      </div>
      </div>
    </div>
  )
}
